import React, { useEffect, useState } from "react";
import { Repeat } from "lucide-react";
import { toast } from "sonner";
import Modal, { ModalHeader } from "./Modal";
import { api, errMsg } from "../lib/api";

const inputCls = "w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm";
const today = () => new Date().toISOString().slice(0, 10);

export default function PlanSprintsButton({ projectId, project, roles, sprintCount, sprintRoleIds = [], onPlanned }) {
  const cfg = project?.sprint_config;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ length_days: 10, start_date: today(), count: 6, role_ids: [] });
  useEffect(() => {
    if (open) setF((x) => ({ ...x, length_days: cfg?.length_days || x.length_days, start_date: cfg?.start_date || x.start_date, count: cfg ? 1 : 6, role_ids: cfg ? sprintRoleIds : x.role_ids }));
  }, [open, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRole = (id) => setF({ ...f, role_ids: f.role_ids.includes(id) ? f.role_ids.filter((x) => x !== id) : [...f.role_ids, id] });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/sprints/plan`, { ...f, length_days: Number(f.length_days), count: Number(f.count) });
      toast.success(`${f.count} sprint${Number(f.count) === 1 ? "" : "s"} added to the plan`);
      setOpen(false);
      onPlanned(data);
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="plan-sprints-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-slate-700 text-slate-300 hover:border-blue-500">
        <Repeat size={14} /> {cfg ? "Add sprints" : "Plan sprints"}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} size="lg" testId="plan-sprints-modal">
        <ModalHeader icon={Repeat} title={cfg ? `Add sprints after Sprint ${sprintCount}` : "Plan sprints"} onClose={() => setOpen(false)} testId="plan-sprints-close" />
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Sprint length (working days)</span>
              <input type="number" min={1} max={60} required disabled={!!cfg} className={inputCls} value={f.length_days} onChange={(e) => setF({ ...f, length_days: e.target.value })} data-testid="sprint-length-input" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">First sprint starts</span>
              <input type="date" required disabled={!!cfg} className={inputCls} value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="sprint-start-input" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Number of sprints</span>
              <input type="number" min={1} max={52} required className={inputCls} value={f.count} onChange={(e) => setF({ ...f, count: e.target.value })} data-testid="sprint-count-input" />
            </label>
          </div>
          {cfg && <p className="text-xs text-slate-500" data-testid="sprint-config-locked">Sprint length and start date are set for this project ({cfg.length_days} working days from {cfg.start_date}).</p>}
          <div>
            <span className="text-[10px] uppercase tracking-widest text-slate-400">Team roles (each adds its cost to every sprint)</span>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="sprint-roles">
              {roles.length === 0 && <span className="text-xs text-slate-500">No roles yet. Add roles to include team cost in sprints.</span>}
              {roles.map((r) => {
                const on = f.role_ids.includes(r.id);
                return (
                  <button type="button" key={r.id} onClick={() => toggleRole(r.id)} data-testid={`sprint-role-${r.id}`}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-blue-500/20 border-blue-500/60 text-blue-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>{r.name}</button>
                );
              })}
            </div>
          </div>
          <p className="text-xs text-slate-500">Each sprint becomes a block on the Gantt that starts after the previous one. Assign stories to a sprint with the Sprint and Points columns in the task table.</p>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={() => setOpen(false)} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
            <button type="submit" disabled={busy} data-testid="plan-sprints-submit" className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{busy ? "Creating…" : "Create sprints"}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}
